import React, { useState } from "react";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import useQuizReview from "../../../../swr/hooks/Library/useQuizReview";
import styled from "styled-components";
import API from "../../../../axios/instances/API";
import { apiFormatDate, getSrcTwo } from "../../../../storage/helpers";
import { addDays } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Calendar } from "react-feather";
import { DatePicker } from "@mui/x-date-pickers"; //TEST

const ModalWrapper = styled(Paper)`
  padding: 20px;
  width: calc(min(420px, 96vw));
  max-height: 400px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  overflow-y: scroll;
  border: 2px solid black;
`;

const SelectedIndicator = styled.div`
  height: 13px;
  width: 13px;
  min-width: 13px;
  min-height: 13px;
  background: ${(props) => (props.$isSelected ? "#337AB7" : "lightgray")};
  margin-right: 8px;
  border-radius: 4px;
`;

const getMinEndDate = (startDate) => {
  const cleanStartDate = addDays(new Date(startDate), 1);
  const today = new Date();
  const tomorrow = addDays(today, 1);

  if (cleanStartDate > today) {
    return addDays(cleanStartDate, 1);
  }

  return tomorrow;
};

const EditEndDateModal = ({ id, setSnackbarText, handleClose }) => {
  const { details: data, isLoading } = useQuizReview(id);

  const [selectedDate, setSelectedDate] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [open, setOpen] = useState(false);

  const handleUpdateEndDate = async () => {
    try {
      setUpdating(true);
      const sendObj = {
        id,
        due_date: apiFormatDate(selectedDate),
        start_date: data?.startDate,
        name: data?.name,
        description: data?.description,
        estimated_time: data?.estimatedTime,
        language: data?.language,
        disclaimer: data?.disclaimer,
        disclaimer_text: data?.disclaimerText,
        display_answers: data?.displayAnswers,
      };

      await API.put("dashboard/quiz/details/", sendObj);
      setUpdating(false);
      setSnackbarText("Module end date updated success");

      return handleClose();
    } catch (error) {
      setUpdating(false);
      setSnackbarText("Failed to updated module end date error");
    }
  };

  const handleRemoveEndDate = async () => {
    setRemoving(true);
    const sendObj = {
      id,
      due_date: null,
      start_date: data?.startDate,
      name: data?.name,
      description: data?.description,
      estimated_time: data?.estimatedTime,
      language: data?.language,
      disclaimer: data?.disclaimer,
      disclaimer_text: data?.disclaimerText,
      display_answers: data?.displayAnswers,
    };

    try {
      await API.put("dashboard/quiz/details/", sendObj);
      setRemoving(false);
      setSnackbarText("Module end date removed warning");

      return handleClose();
    } catch (error) {
      setRemoving(false);
      setSnackbarText("Failed to remove end date error");
    }
  };

  if (isLoading) {
    return (
      <ModalWrapper>
        <div
          style={{
            width: "100%",
            height: "100%",
            minHeight: 160,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "lightgray" }}>loading...</span>
        </div>
      </ModalWrapper>
    );
  }
  const minimumEndDate = getMinEndDate(data.startDate);

  return (
    <ModalWrapper>
      <div
        style={{
          width: "100%",
          minHeight: 140,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
        }}
      >
        <div
          style={{
            height: 48,
            width: "100%",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <img
            src={getSrcTwo(data.image)}
            style={{
              height: 46,
              width: 46,
              minHeight: 46,
              minWidth: 46,
              background: "white",
              border: "1px solid #eeeeee",
              borderRadius: 8,
              objectFit: "contain",
            }}
            alt="module"
          />
          <div style={{ marginLeft: 6 }}>
            <Typography style={{ fontSize: 14, fontWeight: "600" }}>
              {data.name}
            </Typography>
            <Typography style={{ fontSize: 12 }}>
              {data?.marketPlaceParent?.company?.name}
            </Typography>
          </div>
        </div>
        <span style={{ fontWeight: "500", fontSize: 12, marginBottom: 4 }}>
          Select New End Date
        </span>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disableHighlightToday
            minDate={minimumEndDate}
            sx={{ width: "100%" }}
            closeOnSelect
            format="MMM dd yyyy"
            open={open}
            color="primary"
            disablePast
            onAccept={(date) => {
              setSelectedDate(date);
            }}
            onClose={() => setOpen(false)}
            slots={{
              inputAdornment: () => null,
            }}
            slotProps={{
              textField: {
                placeholder: " ",
                size: "small",
                onClick: () => setOpen(true),
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calendar
                        style={{ height: 14, width: 14, color: "darkgrey" }}
                      />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
        </LocalizationProvider>
        <div style={{ marginBottom: 8, marginTop: 8, padding: 2 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <span style={{ fontSize: 12, color: "darkgrey" }}>
              Module start date:
            </span>
            <span
              style={{
                fontSize: 12,
                color: "black",
                fontWeight: "500",
                marginLeft: 4,
              }}
            >
              {!data.startDate
                ? "None"
                : addDays(new Date(data.startDate), 1).toDateString()}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <span style={{ fontSize: 12, color: "darkgrey" }}>
              Previous selected end date:
            </span>
            <span
              style={{
                fontSize: 12,
                color: "black",
                fontWeight: "500",
                marginLeft: 4,
              }}
            >
              {!data.dueDate
                ? "None"
                : addDays(new Date(data.dueDate), 1).toDateString()}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              marginTop: 6,
            }}
          >
            <span style={{ fontSize: 12, color: "black", fontWeight: "500" }}>
              Updated module end date:
            </span>
            <span
              style={{
                fontSize: 12,
                color: "#337ab7",
                fontWeight: "500",
                marginLeft: 4,
              }}
            >
              {!selectedDate ? "---" : selectedDate.toDateString()}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {Boolean(data?.dueDate) ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={removing}
              style={{
                color: removing ? "darkgrey" : "firebrick",
                fontSize: 11,
              }}
              onClick={handleRemoveEndDate}
            >
              Remove End Date
            </Button>
          ) : (
            <div />
          )}
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!selectedDate || updating}
            onClick={handleUpdateEndDate}
            startIcon={updating ? <CircularProgress size={16} /> : null}
          >
            Update
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default EditEndDateModal;

{
  /* <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <DatePicker
            disableToolbar
            disablePast
            emptyLabel="End Date"
            fullWidth
            minDate={minimumEndDate}
            autoOk
            inputVariant="outlined"
            size="small"
            variant="inline"
            format="yyyy-MM-dd"
            value={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            InputProps={{
              startAdornment: (
                <SelectedIndicator $isSelected={Boolean(selectedDate)} />
              ),
            }}
          />
        </MuiPickersUtilsProvider> */
}
